import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Grid,
  Box,
  Typography,
  Modal,
  Menu,
  MenuItem,
  Divider,
  IconButton,
  Avatar,
  Chip,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { FiX, FiAlertTriangle } from "react-icons/fi";
import { HiOutlineUserGroup } from "react-icons/hi";
import { useQuery } from "@tanstack/react-query";

import Input from "../../../components/Form/Input";
import PhoneInput from "../../../components/Form/PhoneInput";
import Select from "../../../components/Form/Select";
import Button, { Color } from "../../../components/Form/Button";
import { useAppState } from "../../../context/StateContext";
import MembersService from "../../../services/members.service";
import BranchService from "../../../services/branch.service";
import { MemberUser } from "../../../models/members/member.user";
import { TypeOfMember } from "../../../util/enums/members";
import { Status } from "../../../util/enums";
import env from "../../../util/env";
import useActionOnBoarding from "../../../hooks/useActionOnBoarding";
import UploadCSVFile, { UploadCSVFileHandles } from "../../../components/Generic/UploadCSVFile";
import { AddCircle, ExportCurve } from "iconsax-react";
import { colors } from "@zapalacorp/tokens";
import GroupService from "Services/group.service";

type Props = {
  onReadyChange?: (ready: boolean) => void;
};

interface AddedMember {
  id?: number;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  code?: string;
  groupId?: number;
  groupName?: string;
  branchId?: number;
  branchName?: string;
  memberType: string;
}

const accessGroupOptions = [
  { value: "Admin", label: "Admin" },
  { value: "Manager", label: "Manager" },
  { value: "User", label: "User" },
];

const memberTypeOptions = [
  { value: "Admin", label: "Admin" },
  { value: "Employee", label: "Employee" },
  { value: "Contractor", label: "Contractor" },
];

const Members: React.FC<Props> = ({ onReadyChange }) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  // Buscar branches da API
  const { data: branchesData, isLoading: loadingBranches } = useQuery({
    queryKey: ["branches", currentUser?.companyId],
    queryFn: async () => {
      if (!currentUser?.companyId) return [];

      const response = await BranchService.getAll({
        page: 0,
        pageSize: 100,
        status: Status.ACTIVE,
        companyId: currentUser.companyId,
      });

      return response.data || [];
    },
    enabled: !!currentUser?.companyId,
  });

  const branchOptions = React.useMemo(() => {
    if (!branchesData) return [];

    return branchesData.map((branch) => ({
      value: branch.id?.toString() || "",
      label: branch.name || "",
    }));
  }, [branchesData]);

  // Buscar groups da API
  const { data: groupsData, isLoading: loadingGroups } = useQuery({
    queryKey: ["groups", currentUser?.companyId],
    queryFn: async () => {
      if (!currentUser?.companyId) return [];

      const response = await GroupService.getGroups({
        status: 1,
        type: "System",
        companyId: currentUser?.companyId,
      });

      if (response && response?.data) {
        const orderedData = response.data?.sort((a: any, b: any) => {
          const nameA = a?.name?.toUpperCase();
          const nameB = b?.name?.toUpperCase();

          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });
        return orderedData;
      }

      return [];
    },
    enabled: !!currentUser?.companyId,
  });

  const groupOptions = React.useMemo(() => {
    if (!groupsData) return [];

    return groupsData.map((group) => ({
      value: group.id?.toString() || "",
      label: group.name || "",
    }));
  }, [groupsData]);

  const { getCompanyFirstBranch } = useActionOnBoarding();
  const { data: firstBranch } = getCompanyFirstBranch();

  const [memberData, setMemberData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    memberCode: "",
    branch: "",
    mobile: "",
    accessGroup: "",
    memberType: "",
  });

  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState<null | HTMLElement>(null);
  const [ready, setReady] = useState(true);
  const [addedMembers, setAddedMembers] = useState<AddedMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [csvErrors, setCsvErrors] = useState<Array<{ line: number; error: string }>>([]);
  const [showCsvErrorModal, setShowCsvErrorModal] = useState(false);
  const [csvFileName, setCsvFileName] = useState("");
  const [uploadingCSV, setUploadingCSV] = useState(false);
  const uploadCSVRef = useRef<UploadCSVFileHandles>(null);

  const [modalMemberData, setModalMemberData] = useState({
    firstName: "",
    lastName: "",
    userCode: "",
    email: "",
    group: "",
    branch: "",
    mobile: "",
    memberType: "",
  });

  useEffect(() => {
    onReadyChange?.(ready);
  }, [ready, onReadyChange]);

  const handleInputChange = useCallback((field: string, value: string | number) => {
    setMemberData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleModalInputChange = useCallback((field: string, value: string) => {
    setModalMemberData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleGenerateMemberCode = useCallback(() => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    setMemberData((prev) => ({
      ...prev,
      memberCode: code,
    }));
  }, []);

  const handleGenerateModalCode = useCallback(() => {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    setModalMemberData((prev) => ({
      ...prev,
      userCode: code,
    }));
  }, []);

  const handleUploadCSV = useCallback(() => {
    console.log("📤 Opening CSV upload modal...");
    setUploadMenuAnchor(null);
    uploadCSVRef.current?.open();
  }, []);

  const handleDownloadTemplate = useCallback(() => {
    setUploadMenuAnchor(null);

    // Download do template de usuários/staff
    const templateUrl = env.LINK_TEMPLATE_USER_CSV || env.LINK_TEMPLATES_ZIP;

    if (templateUrl) {
      const link = document.createElement("a");
      link.href = templateUrl;
      link.setAttribute("download", "shipeezi_members_template.csv");
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log("✅ Downloading members template from:", templateUrl);
    } else {
      console.warn("⚠️ Template URL not configured");
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    // Limpar formulário modal
    setModalMemberData({
      firstName: "",
      lastName: "",
      userCode: "",
      email: "",
      group: "",
      branch: "",
      mobile: "",
      memberType: "",
    });

    setShowAddMemberModal(false);
  }, []);

  const handleAddMember = useCallback(async () => {
    if (!currentUser?.companyId) {
      console.error("Company ID not found");
      return;
    }

    try {
      setLoading(true);

      const modalDataBranch = branchesData?.find(
        (branch) => branch.id === Number(modalMemberData.branch) || branch.id === firstBranch?.id
      );

      const memberBranches = modalDataBranch ? [modalDataBranch] : currentUser.branches || [];

      const newMember: MemberUser = {
        firstName: modalMemberData.firstName,
        lastName: modalMemberData.lastName,
        email: modalMemberData.email,
        mobile: modalMemberData.mobile,
        code: modalMemberData.userCode,
        username: modalMemberData.email,
        typeOfUser: modalMemberData.memberType,
        status: Status.ACTIVE,
        timezoneId: currentUser.timezoneId || 1,
        branches: memberBranches,
        leader: false,
        roleId: 1, // Default role
        typeOfMember: TypeOfMember.USER,
        groupId: Number(modalMemberData.group) || 0,
      } as MemberUser;

      const response = await MembersService.createMember(newMember);

      if (response.data) {
        console.log("✅ Member created successfully:", response.data);

        const addedMemberGroup = groupsData?.find(
          (group) => group.id === Number(modalMemberData.group)
        );

        // Adicionar à lista local
        setAddedMembers((prev) => [
          ...prev,
          {
            firstName: modalMemberData.firstName,
            lastName: modalMemberData.lastName,
            email: modalMemberData.email,
            mobile: modalMemberData.mobile,
            code: modalMemberData.userCode,
            groupName: addedMemberGroup?.name || "General",
            branchName: modalMemberData.branch,
            memberType: modalMemberData.memberType,
          },
        ]);

        handleCloseModal();
      }
    } catch (error) {
      console.error("❌ Error creating member:", error);
    } finally {
      setLoading(false);
    }
  }, [modalMemberData, currentUser, setAddedMembers]);

  return (
    <Box sx={{ width: "100%" }}>
      <Grid container spacing={2}>
        {/* Header with Icon */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: "#101828", mb: 2 }}>
                {translate("onboarding.members.title", { defaultValue: "Members" })}
              </Typography>
              <Typography variant="body1" sx={{ color: "#475467", mb: 3 }}>
                {translate("onboarding.members.description", {
                  defaultValue:
                    "Invite the people who will be using Shipeezi with you. Assign their role and branch so they have the right access from the start. You can always update or add more members later.",
                })}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                border: `2px solid ${colors.brandBlue600}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#F0F7FF",
                flexShrink: 0,
                ml: 2,
              }}
            >
              <HiOutlineUserGroup size={28} color={colors.brandBlue600} />
            </Box>
          </Box>
        </Grid>

        {/* Registered Members Section */}
        <Grid item xs={12}>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 400, color: colors.brandBlue600, fontSize: "16px" }}
              >
                {translate("onboarding.members.registeredMembers", {
                  defaultValue: "Registered members",
                })}
              </Typography>
              <Chip
                label={addedMembers.length + 1}
                sx={{
                  height: "24px",
                  minWidth: "24px",
                  borderRadius: "50%",
                  backgroundColor: colors.brandBlue600,
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: "12px",
                  "& .MuiChip-label": {
                    px: "8px",
                  },
                }}
              />
            </Box>

            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Input
                    required
                    label={translate("onboarding.members.firstName", {
                      defaultValue: "First Name",
                    })}
                    value={memberData.firstName}
                    onChange={(e) => handleInputChange("firstName", e.target.value)}
                    labelClassName="onboarding-blue-label"
                    placeholder="John"
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Input
                    required
                    label={translate("onboarding.members.lastName", { defaultValue: "Last Name" })}
                    value={memberData.lastName}
                    onChange={(e) => handleInputChange("lastName", e.target.value)}
                    labelClassName="onboarding-blue-label"
                    placeholder="Doe"
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12} sm={4}>
                  <Input
                    required
                    label={translate("onboarding.members.email", { defaultValue: "Email" })}
                    value={memberData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    labelClassName="onboarding-blue-label"
                    type="email"
                    placeholder="email@email.com"
                  />
                </Grid>

                {/* Member Code */}
                <Grid item xs={12} sm={4}>
                  <Input
                    required
                    label={translate("onboarding.members.memberCode", {
                      defaultValue: "Member code",
                    })}
                    value={memberData.memberCode}
                    onChange={(e) => handleInputChange("memberCode", e.target.value)}
                    labelClassName="onboarding-blue-label"
                    placeholder="Auto-generate"
                    InputProps={{
                      endAdornment: (
                        <Box
                          component="button"
                          onClick={handleGenerateMemberCode}
                          sx={{
                            background: "none",
                            border: "none",
                            color: colors.brandBlue600,
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: 500,
                            padding: "8px 12px",
                            "&:hover": {
                              color: "#003d82",
                            },
                          }}
                        >
                          {translate("generate")}
                        </Box>
                      ),
                    }}
                  />
                </Grid>

                {/* Branch */}
                <Grid item xs={12} sm={4}>
                  <Select
                    label={translate("onboarding.members.branch", { defaultValue: "Branch" })}
                    value={firstBranch?.id?.toString() || ""}
                    onChange={(e) => handleInputChange("branch", e.target.value as number)}
                    labelStyle={{ color: colors.brandBlue600, fontWeight: 600 }}
                    disabled
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      {translate("selectBranch")}
                    </MenuItem>
                    {branchOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                {/* Mobile */}
                <Grid item xs={12} sm={4}>
                  <PhoneInput
                    required
                    label={translate("onboarding.members.mobile", { defaultValue: "Mobile" })}
                    value={memberData.mobile}
                    onChange={(value) => handleInputChange("mobile", value || "")}
                    labelStyle="onboarding-blue-label"
                    defaultCountry="AU"
                  />
                </Grid>

                {/* Access Group */}
                <Grid item xs={12} sm={6}>
                  <Select
                    required
                    label={translate("onboarding.members.accessGroup", {
                      defaultValue: "Access Group",
                    })}
                    value={memberData.accessGroup}
                    onChange={(e) => handleInputChange("accessGroup", e.target.value as string)}
                    labelStyle={{ color: colors.brandBlue600, fontWeight: 600 }}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      {translate("selectGroup")}
                    </MenuItem>
                    {accessGroupOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                {/* Member Type */}
                <Grid item xs={12} sm={6}>
                  <Select
                    required
                    label={translate("onboarding.members.memberType", {
                      defaultValue: "Member Type",
                    })}
                    value={memberData.memberType}
                    onChange={(e) => handleInputChange("memberType", e.target.value as string)}
                    labelStyle={{ color: colors.brandBlue600, fontWeight: 600 }}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      {translate("selectType")}
                    </MenuItem>
                    {memberTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>

        {/* Additional Members Section */}
        <Grid item xs={12}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: colors.gray800,
                fontSize: "24px",
                lineHeight: "32px",
                fontFamily: "Manrope",
              }}
            >
              {translate("onboarding.members.additionalMembers", {
                defaultValue: "Additional Members",
              })}
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                customColor={Color.GRAY_600_TEXT}
                variant="outlined"
                startIcon={<ExportCurve size={20} />}
                onClick={(e) => setUploadMenuAnchor(e.currentTarget)}
                sx={{
                  borderColor: colors.gray600,
                  color: colors.gray600,
                  backgroundColor: "#FFFFFF",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "16px",
                  lineHeight: "20px",
                  fontFamily: "Manrope",
                }}
              >
                {translate("onboarding.members.uploadMembers", { defaultValue: "Upload members" })}
              </Button>
              <Button
                customColor={Color.BRAND_OUTLINED}
                variant="outlined"
                startIcon={<AddCircle size={20} />}
                onClick={() => setShowAddMemberModal(true)}
                sx={{
                  borderColor: colors.brandBlue600,
                  color: colors.brandBlue600,
                  backgroundColor: "#FFFFFF",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "16px",
                  lineHeight: "20px",
                  fontFamily: "Manrope",
                }}
              >
                {translate("onboarding.members.addMembers", { defaultValue: "Add members" })}
              </Button>
            </Box>
          </Box>

          <Box sx={{ overflow: "hidden" }}>
            {/* Table Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1fr",
                gap: 2,
                p: "12px 0",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: colors.gray800,
                  fontSize: "14px",
                  fontFamily: "Manrope",
                }}
              >
                {translate("onboarding.members.name", { defaultValue: "Name" })}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: colors.gray800,
                  fontSize: "14px",
                  fontFamily: "Manrope",
                }}
              >
                {translate("onboarding.members.group", { defaultValue: "Group" })}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: colors.gray800,
                  fontSize: "14px",
                  fontFamily: "Manrope",
                }}
              >
                {translate("onboarding.members.type", { defaultValue: "Type" })}
              </Typography>
            </Box>

            {/* Table Content */}
            {addedMembers.length === 0 ? (
              <Box sx={{ textAlign: "flex-start" }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Manrope",
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: colors.gray800,
                  }}
                >
                  {translate("onboarding.members.noAdditionalMembers", {
                    defaultValue: "No additional members so far.",
                  })}
                </Typography>
              </Box>
            ) : (
              addedMembers.map((member, index) => {
                const avatarColor = ["#7F56D9", "#2E90FA", "#15B79E", "#F04438", "#F79009"][
                  index % 5
                ];
                const initial = member.firstName.charAt(0).toUpperCase();

                return (
                  <Box
                    key={index}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1fr",
                      gap: 2,
                      p: "4px 8px",
                      alignItems: "center",
                      backgroundColor: index % 2 === 0 ? "none" : colors.gray50,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          backgroundColor: avatarColor,
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        {initial}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "#101828", fontWeight: 500, fontSize: "14px" }}
                        >
                          {member.firstName} {member.lastName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#667085", fontSize: "14px" }}>
                          {member.email}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: "#475467", fontSize: "14px" }}>
                      {member.groupName || "General"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#475467", fontSize: "14px" }}>
                      {member.memberType || "General"}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Upload Menu */}
      <Menu
        anchorEl={uploadMenuAnchor}
        open={Boolean(uploadMenuAnchor)}
        onClose={() => setUploadMenuAnchor(null)}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            borderRadius: 2,
          },
        }}
      >
        <MenuItem
          onClick={handleDownloadTemplate}
          sx={{
            backgroundColor: "#E0F2FE",
            "&:hover": {
              backgroundColor: "#BAE6FD",
            },
          }}
        >
          {translate("onboarding.members.downloadTemplate", {
            defaultValue: "Download our CSV template",
          })}
        </MenuItem>
        <MenuItem
          onClick={handleUploadCSV}
          sx={{
            "&:hover": {
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          {translate("onboarding.members.uploadCSV", { defaultValue: "Upload CSV" })}
        </MenuItem>
      </Menu>

      {/* Add Member Modal */}
      <Modal
        open={showAddMemberModal}
        onClose={() => handleCloseModal()}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 600,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}
          >
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Manrope",
                fontWeight: 800,
                color: colors.gray700,
                fontSize: "20px",
                lineHeight: "28px",
              }}
            >
              {translate("onboarding.members.addMember", { defaultValue: "Add Member" })}
            </Typography>
            <Button
              onClick={() => handleCloseModal()}
              customColor={Color.SEND}
              sx={{
                minWidth: "auto",
                p: 1,
                borderRadius: "50%",
                color: "#667085",
                "&:hover": {
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              <FiX size={24} color={colors.black} />
            </Button>
          </Box>

          <Grid container spacing={2}>
            {/* First Name */}
            <Grid item xs={4}>
              <Input
                required
                label={translate("onboarding.members.firstName", { defaultValue: "First name" })}
                placeholder="Name"
                value={modalMemberData.firstName}
                onChange={(e) => handleModalInputChange("firstName", e.target.value)}
                labelClassName="onboarding-blue-label"
              />
            </Grid>

            {/* Last Name */}
            <Grid item xs={4}>
              <Input
                required
                label={translate("onboarding.members.lastName", { defaultValue: "Last name" })}
                placeholder="Last Name"
                value={modalMemberData.lastName}
                onChange={(e) => handleModalInputChange("lastName", e.target.value)}
                labelClassName="onboarding-blue-label"
              />
            </Grid>

            {/* Email */}
            <Grid item xs={4}>
              <Input
                required
                label={translate("onboarding.members.email", { defaultValue: "Email" })}
                placeholder="email@email.com"
                value={modalMemberData.email}
                onChange={(e) => handleModalInputChange("email", e.target.value)}
                labelClassName="onboarding-blue-label"
                type="email"
              />
            </Grid>

            {/* User Code */}
            <Grid item xs={4}>
              <Input
                required
                label={translate("onboarding.members.userCode", { defaultValue: "User code" })}
                placeholder="-"
                value={modalMemberData.userCode}
                onChange={(e) => handleModalInputChange("userCode", e.target.value)}
                labelClassName="onboarding-blue-label"
                InputProps={{
                  endAdornment: (
                    <Box
                      component="button"
                      onClick={handleGenerateModalCode}
                      sx={{
                        background: "none",
                        border: "none",
                        color: colors.brandBlue600,
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: 500,
                        padding: "8px 12px",
                        "&:hover": {
                          color: "#003d82",
                        },
                      }}
                    >
                      {translate("generate")}
                    </Box>
                  ),
                }}
              />
            </Grid>

            {/* Branch */}
            <Grid item xs={4}>
              <Select
                label={translate("onboarding.members.branch", { defaultValue: "Branch" })}
                value={modalMemberData.branch || firstBranch?.id?.toString() || ""}
                onChange={(e) => handleModalInputChange("branch", e.target.value as string)}
                labelStyle={{ color: colors.brandBlue600, fontWeight: 600 }}
                displayEmpty
                disabled={loadingBranches}
              >
                <MenuItem value="" disabled>
                  {loadingBranches ? "Loading..." : translate("selectBranch")}
                </MenuItem>
                {branchOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {/* Mobile */}
            <Grid item xs={4}>
              <PhoneInput
                required
                label={translate("onboarding.members.mobile", { defaultValue: "Mobile" })}
                placeholder="000000000"
                value={modalMemberData.mobile}
                onChange={(value) => handleModalInputChange("mobile", value || "")}
                labelStyle="onboarding-blue-label"
                defaultCountry="AU"
              />
            </Grid>

            {/* Group */}
            <Grid item xs={6}>
              <Select
                required
                label={translate("onboarding.members.group", { defaultValue: "Group" })}
                value={modalMemberData.group}
                onChange={(e) => handleModalInputChange("group", e.target.value as string)}
                labelStyle={{ color: colors.brandBlue600, fontWeight: 600 }}
                displayEmpty
                disabled={loadingGroups}
              >
                <MenuItem value="" disabled>
                  {translate("selectGroup")}
                </MenuItem>
                {groupOptions.map((group) => (
                  <MenuItem key={group.value} value={group.value}>
                    {group.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {/* Member Type */}
            <Grid item xs={6}>
              <Select
                required
                label={translate("onboarding.members.memberType", { defaultValue: "Member Type" })}
                value={modalMemberData.memberType}
                onChange={(e) => handleModalInputChange("memberType", e.target.value as string)}
                labelStyle={{ color: colors.brandBlue600, fontWeight: 600 }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  {translate("selectType")}
                </MenuItem>
                <MenuItem value="General">General</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Employee">Employee</MenuItem>
              </Select>
            </Grid>
          </Grid>

          {/* Modal Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}>
            <Button
              customColor={Color.TEXT_INFO}
              onClick={() => handleCloseModal()}
              disabled={loading}
              sx={{
                color: "#344054",
                backgroundColor: "#FFFFFF",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "16px",
                height: "44px",
                minWidth: "120px",
                borderRadius: "8px",
              }}
            >
              {translate("common.close", { defaultValue: "Close" })}
            </Button>

            <Button
              customColor={Color.BRAND}
              onClick={handleAddMember}
              disabled={
                loading ||
                !modalMemberData.firstName ||
                !modalMemberData.lastName ||
                !modalMemberData.email ||
                !modalMemberData.mobile ||
                !modalMemberData.memberType ||
                !modalMemberData.group
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "16px",
                height: "44px",
                minWidth: "120px",
                borderRadius: "8px",
                border: "none",
                "&:disabled": {
                  backgroundColor: "#D0D5DD",
                  color: "#667085",
                  opacity: 1,
                },
              }}
            >
              {loading
                ? translate("common.saving", { defaultValue: "Saving..." })
                : translate("common.save", { defaultValue: "Save" })}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* CSV Error Modal */}
      <Modal
        open={showCsvErrorModal}
        onClose={() => setShowCsvErrorModal(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 540,
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  backgroundColor: colors.warning200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FiAlertTriangle size={24} color={colors.warning500} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Manrope",
                    fontWeight: 800,
                    color: colors.black,
                    fontSize: "20px",
                    lineHeight: "28px",
                  }}
                >
                  {translate("errorsFound")}
                </Typography>
              </Box>
            </Box>

            <IconButton
              size="small"
              onClick={() => setShowCsvErrorModal(false)}
              sx={{ color: "#667085" }}
            >
              <FiX size={24} color={colors.black} />
            </IconButton>
          </Box>

          {/* Error List */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                color: colors.black,
                fontSize: "16px",
                lineHeight: "24px",
                mb: 0.5,
              }}
            >
              <Trans
                i18nKey="onCSVWeHaveFoundSomeErrors"
                values={{ csvFileName: csvFileName.split(".csv")?.[0] || "*name of file*" }}
              />
            </Typography>
            {csvErrors.map((error, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  color: colors.warning500,
                  fontFamily: "Inter",
                  fontSize: "16px",
                  lineHeight: "24px",
                  fontStyle: "italic",
                  fontWeight: 400,
                  mb: 0.5,
                }}
              >
                Line {error.line} - {error.error}
              </Typography>
            ))}
          </Box>

          {/* Download Template Link */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                color: colors.black,
                fontSize: "16px",
                lineHeight: "24px",
              }}
            >
              {translate("donTKnowWhatSWrong")}{" "}
              <Box
                component="a"
                href={env.LINK_TEMPLATE_USER_CSV || env.LINK_TEMPLATES_ZIP}
                download
                sx={{
                  color: colors.blue500,
                  textDecoration: "underline",
                  fontWeight: 500,
                  "&:hover": {
                    color: colors.blue500,
                    textDecoration: "underline",
                  },
                }}
              >
                {translate("downloadOurTemplateHere")}
              </Box>
            </Typography>
          </Box>

          {/* Modal Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              customColor={Color.SEND}
              onClick={() => setShowCsvErrorModal(false)}
              sx={{
                color: colors.black,
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: "24px",
              }}
            >
              {translate("cancel")}
            </Button>

            <Button
              customColor={Color.WARNING}
              onClick={() => {
                setShowCsvErrorModal(false);
                handleUploadCSV();
              }}
              sx={{
                backgroundColor: colors.warning500,
                p: "12px 20px",
                color: "#FFFFFF",
                fontFamily: "Inter",
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: "24px",
              }}
            >
              {translate("tryAgain2")}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Upload CSV File Modal */}
      <UploadCSVFile
        ref={uploadCSVRef}
        modalTitle={translate("onboarding.members.uploadMembers", {
          defaultValue: "Upload Members CSV",
        })}
        loadingUpload={uploadingCSV}
        onDownloadTemplate={handleDownloadTemplate}
        onConfirmUpload={async (csvContent, file) => {
          if (!file) {
            console.error("❌ No file provided");
            return;
          }

          console.log("📄 CSV file uploaded:", file.name);
          setUploadingCSV(true);
          setCsvFileName(file.name);

          try {
            // Parse CSV content
            const lines = csvContent.split("\n").filter((line) => line.trim());
            if (lines.length < 2) {
              throw new Error("CSV file is empty or has no data rows");
            }

            // Get headers (first line)
            const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
            console.log("📋 CSV Headers:", headers);

            // Parse data rows
            const errors: Array<{ line: number; error: string }> = [];
            const newMembers: AddedMember[] = [];

            for (let i = 1; i < lines.length; i++) {
              const values = lines[i].split(",").map((v) => v.trim());

              // Map CSV columns to member object
              const firstName =
                values[headers.indexOf("firstname")] ||
                values[headers.indexOf("first name")] ||
                values[headers.indexOf("name")] ||
                "";
              const lastName =
                values[headers.indexOf("lastname")] || values[headers.indexOf("last name")] || "";
              const email = values[headers.indexOf("email")] || "";
              const mobile =
                values[headers.indexOf("mobile")] || values[headers.indexOf("phone")] || "";
              const code =
                values[headers.indexOf("code")] || values[headers.indexOf("user code")] || "";
              const groupName =
                values[headers.indexOf("group")] ||
                values[headers.indexOf("access group")] ||
                "General";
              const memberType =
                values[headers.indexOf("type")] ||
                values[headers.indexOf("member type")] ||
                values[headers.indexOf("role")] ||
                "General";
              const branchName = values[headers.indexOf("branch")] || "Branch 1";

              // Validate required fields
              if (!firstName || !lastName || !email) {
                errors.push({
                  line: i + 1,
                  error: `Missing required fields (firstname, lastname, or email)`,
                });
                continue;
              }

              // Validate email format
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(email)) {
                errors.push({
                  line: i + 1,
                  error: `Invalid email format: ${email}`,
                });
                continue;
              }

              newMembers.push({
                firstName,
                lastName,
                email,
                mobile,
                code: code || `USR${Date.now()}${i}`,
                groupName,
                memberType,
                branchName,
              });
            }

            console.log(`✅ Parsed ${newMembers.length} members from CSV`);
            console.log(`⚠️ Found ${errors.length} errors`);

            if (errors.length > 0) {
              // Show error modal
              setCsvErrors(errors);
              setShowCsvErrorModal(true);
              setUploadingCSV(false);
              uploadCSVRef.current?.close();
              return;
            }

            // Add members to the list
            setAddedMembers((prev) => [...prev, ...newMembers]);

            // Close modal
            setUploadingCSV(false);
            uploadCSVRef.current?.close();

            console.log(`🎉 Successfully added ${newMembers.length} members!`);
          } catch (error) {
            console.error("❌ Error processing CSV:", error);
            setCsvErrors([
              {
                line: 1,
                error: error instanceof Error ? error.message : "Failed to process CSV file",
              },
            ]);
            setShowCsvErrorModal(true);
            setUploadingCSV(false);
            uploadCSVRef.current?.close();
          }
        }}
        onCloseModal={() => {
          console.log("❌ Upload modal closed");
          setUploadingCSV(false);
        }}
      />
    </Box>
  );
};

export default Members;
